import { useState } from "react";
import styled from "styled-components";
import SearchDropdown from "../../components/controls/search-dropdown";
import NumberInput from "../../components/controls/number-input";
import TextInput from "../../components/controls/text-input";
import Button from "../../components/controls/button";
import { FeatureCard, FeatureCardRow, FeatureCardSingleColumn, FeatureCardTitle } from "../../styles/shared/card";
import { ButtonGroup, Footer, MainContent } from "../../styles/shared/drawer";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppSelector } from "../../hooks/useAppSelector";
import { ApplicationSupplierHeader } from "../../models/supplier/application-supplier-header";
import { ApplicationSupplierProductHeader } from "../../models/supplier/application-supplier-product-header";
import { fetchSuppliers, resetSuppliers } from "../../store/features/supplier/supplier-slice";
import {
  fetchSupplierProducts,
  resetSupplierProducts,
} from "../../store/features/supplier/supplier-product-slice";
import { ApplicationReturn } from "../../models/returns/applicant-return";
import { minValueValidator, requiredValidator } from "../../helpers/validators";
import { insertReturn } from "../../store/features/returns/returns-slice";
import { showSuccess } from "../../components/notification/toastr-actions";
import { closeDrawer } from "../../store/features/drawer/drawer-slice";

interface ValidationState {
  supplier: string | null;
  supplierProduct: string | null;
  returnQuantity: string | null;
}

export default function CreateReturnDrawer() {
  const dispatch = useAppDispatch();
  const supplierState = useAppSelector((state) => state.supplier);
  const branchState = useAppSelector((state) => state.branch);
  const supplierProductState = useAppSelector((state) => state.supplierProducts);

  const [selectedSupplier, setSelectedSupplier] = useState<ApplicationSupplierHeader | null>(null);
  const [selectedSupplierProduct, setSelectedSupplierProduct] =
    useState<ApplicationSupplierProductHeader | null>(null);
  const [returnQuantity, setReturnQuantity] = useState<number | null>(null);
  const [notes, setNotes] = useState<string>("");
  const [validation, setValidation] = useState<ValidationState>({
    supplier: null,
    supplierProduct: null,
    returnQuantity: null,
  });

  const fetchSupplierOptions = async (query: string, reset: boolean = true): Promise<void> => {
    await dispatch(fetchSuppliers({ fetchParams: { reset, searchText: query } }));
  };

  const fetchSupplierProductOptions = async (query: string, reset: boolean = true): Promise<void> => {
    if (!selectedSupplier) {
      dispatch(resetSupplierProducts());
      return;
    }

    await dispatch(
      fetchSupplierProducts({
        supplierId: selectedSupplier.id,
        unmappedOnly: false,
        fetchParams: {
          reset,
          searchText: query,
        },
      })
    );
  };

  const onSupplierChange = (supplier: ApplicationSupplierHeader | null) => {
    setSelectedSupplier(supplier);
    setSelectedSupplierProduct(null);
    dispatch(resetSupplierProducts());
    setValidation((prev) => ({
      ...prev,
      supplier: requiredValidator(supplier?.id),
      supplierProduct: null,
    }));
  };

  const onSupplierProductChange = (supplierProduct: ApplicationSupplierProductHeader | null) => {
    setSelectedSupplierProduct(supplierProduct);
    setValidation((prev) => ({
      ...prev,
      supplierProduct: requiredValidator(supplierProduct?.id),
    }));
  };

  const validate = () => {
    const nextValidation: ValidationState = {
      supplier: requiredValidator(selectedSupplier?.id),
      supplierProduct: requiredValidator(selectedSupplierProduct?.id),
      returnQuantity: minValueValidator(returnQuantity, 1),
    };

    setValidation(nextValidation);

    return !Object.values(nextValidation).some((x) => x != null);
  };

  const createReturn = async () => {
    if (!selectedSupplier || !selectedSupplierProduct) {
      validate();
      return;
    }

    if (!validate()) {
      return;
    }

    const request: ApplicationReturn = {
      id: 0,
      branchId: branchState.selectedBranch!.id,
      branchName: branchState.selectedBranch!.name,
      supplierId: selectedSupplier.id,
      supplierName: selectedSupplier.name,
      supplierProductId: selectedSupplierProduct.id,
      productName: selectedSupplierProduct.name,
      returnQuantity: returnQuantity ?? 0,
      notes: notes.length > 0 ? notes : null,
      status: "New",
      created: new Date(),
      createdBy: null,
      updated: null,
      updatedBy: null,
    };

    const response = await dispatch(insertReturn(request));
    if (insertReturn.fulfilled.match(response)) {
      dispatch(showSuccess("Return created"));
      dispatch(closeDrawer());
    }
  };

  return (
    <Container>
      <MainContent>
        <FeatureCard>
          <FeatureCardTitle>Selection</FeatureCardTitle>
          <CardBody>
            <FeatureCardRow>
              <FeatureCardSingleColumn>
                <FieldsStack>
                  <FieldGroup>
                    <SearchDropdown<ApplicationSupplierHeader>
                      fetchOptions={fetchSupplierOptions}
                      clearOptions={() => dispatch(resetSuppliers())}
                      options={supplierState.suppliers?.suppliers ?? []}
                      hasMore={(supplierState.suppliers?.currentPosition ?? 0) < (supplierState.suppliers?.count ?? 0)}
                      label="Search suppliers"
                      onChange={onSupplierChange}
                      isOptionEqualToValue={(option, value) => option.id == value.id}
                      renderOption={(props, option) => (
                        <StyledOption {...props}>
                          <OptionText>{option.name}</OptionText>
                        </StyledOption>
                      )}
                    />
                    {validation.supplier && <ErrorText>{validation.supplier}</ErrorText>}
                  </FieldGroup>

                  <FieldGroup>
                    <SearchDropdown<ApplicationSupplierProductHeader>
                      fetchOptions={fetchSupplierProductOptions}
                      clearOptions={() => dispatch(resetSupplierProducts())}
                      options={supplierProductState.products?.products ?? []}
                      hasMore={(supplierProductState.products?.currentPosition ?? 0) < (supplierProductState.products?.count ?? 0)}
                      label="Search supplier products"
                      onChange={onSupplierProductChange}
                      isOptionEqualToValue={(option, value) => option.id == value.id}
                      renderOption={(props, option) => (
                        <StyledOption {...props}>
                          <OptionImage src={option.image} alt={option.name} />
                          <OptionText>{option.name}</OptionText>
                        </StyledOption>
                      )}
                    />
                    {validation.supplierProduct && <ErrorText>{validation.supplierProduct}</ErrorText>}
                  </FieldGroup>
                </FieldsStack>
              </FeatureCardSingleColumn>
            </FeatureCardRow>
          </CardBody>


        </FeatureCard>

        <FeatureCard>
          <FeatureCardTitle>Return Details</FeatureCardTitle>
          <CardBody>
            {(selectedSupplier || selectedSupplierProduct) && (
              <FeatureCardRow>
                <FeatureCardSingleColumn>
                  <SelectionSummary>
                    {selectedSupplier && <SummaryItem>Supplier: {selectedSupplier.name}</SummaryItem>}
                    {selectedSupplierProduct && <SummaryItem>Product: {selectedSupplierProduct.name}</SummaryItem>}
                  </SelectionSummary>
                </FeatureCardSingleColumn>
              </FeatureCardRow>
            )}
            <FeatureCardRow>
              <FeatureCardSingleColumn>
                <FieldsStack>
                  <FieldGroup>
                    <NumberInput
                      id="txtReturnQuantity"
                      label="Return Quantity"
                      min={1}
                      value={returnQuantity}
                      onChange={(value) => {
                        setReturnQuantity(value);
                        setValidation((prev) => ({
                          ...prev,
                          returnQuantity: minValueValidator(value, 1),
                        }));
                      }}
                      error={validation.returnQuantity}
                    />
                  </FieldGroup>

                  <FieldGroup>
                    <TextInput
                      id="txtReturnNotes"
                      label="Notes (optional)"
                      value={notes}
                      multiline
                      rows={4}
                      onChange={(event) => setNotes(event.target.value)}
                    />
                  </FieldGroup>
                </FieldsStack>
              </FeatureCardSingleColumn>
            </FeatureCardRow>
          </CardBody>
        </FeatureCard>
      </MainContent>

      <Footer>
        <ButtonGroup>
          <Button
            id="btnCancelReturn"
            label="Cancel"
            variant="outlined"
            onClick={() => dispatch(closeDrawer())}
          />
          <Button
            id="btnCreateReturn"
            label="Create Return"
            variant="contained"
            onClick={createReturn}
          />
        </ButtonGroup>
      </Footer>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;

  ${MainContent} {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
`;

const IntroCard = styled.div`
  padding: 14px;
  border-radius: 8px;
  border: 1px solid ${(props) => props.theme.application.border};
  background: ${(props) => props.theme.application.hover};
`;

const IntroTitle = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
`;

const IntroText = styled.div`
  font-size: 0.9rem;
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 2px 0 6px;
`;

const FieldsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
`;

const FieldGroup = styled.div`
  width: 100%;
`;

const SelectionSummary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SummaryItem = styled.div`
  font-size: 0.8rem;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid ${(props) => props.theme.application.border};
  background: ${(props) => props.theme.application.scheme.contrast};
`;

const StyledOption = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const OptionImage = styled.img`
  width: 28px;
  height: 28px;
  object-fit: cover;
  border-radius: 4px;
`;

const OptionText = styled.div`
  font-size: 0.9rem;
`;

const ErrorText = styled.div`
  color: #d32f2f;
  font-size: 0.75rem;
  margin-top: 4px;
`;
